<script setup>
import { onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'

const toast = useToast()
const apiBase = 'https://ncht.bz'

const SUMMARY_PAYMENT_TYPES = [
  'Безналичный расчёт',
  'Крипта',
  'РесурсРазвития (физ.лицо)',
  'РесурсРазвития р/с',
  'Робокасса',
  'ТБанк рассрочка',
  'PayToday',
  'Итого'
]

const STATUS_LABELS = ['Платежи', 'Возвраты']

const loading = ref(false)
const rows = ref([])
const statusRows = ref([])
const dateFrom = ref(null)
const dateTo = ref(null)

function formatMoney(value) {
  const num = Number(value || 0)
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num)
}

function formatPercent(value) {
  const num = Number(value || 0)
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(num)
}

function formatDateForApi(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function buildParams() {
  const params = {}
  const from = formatDateForApi(dateFrom.value)
  const to = formatDateForApi(dateTo.value)
  if (from) params.date_from = from
  if (to) params.date_to = to
  return params
}

async function loadSummary() {
  loading.value = true
  try {
    const { data } = await axios.get(`${apiBase}/api/analytics/payments-v2/summary`, {
      params: buildParams()
    })
    const byType = Object.fromEntries((data?.data || []).map((row) => [row.payment_type, row]))
    rows.value = SUMMARY_PAYMENT_TYPES.map((paymentType) => {
      const row = byType[paymentType]
      if (row) return row
      return {
        payment_type: paymentType,
        sum_amount: 0,
        sum_commissions: 0,
        commission_percent: 0,
        is_total: paymentType === 'Итого'
      }
    })

    const byStatus = Object.fromEntries((data?.status_data || []).map((row) => [row.label, row]))
    statusRows.value = STATUS_LABELS.map((label) => {
      const row = byStatus[label]
      if (row) return row
      return {
        label,
        sum_amount: 0,
        sum_commissions: 0,
        commission_percent: 0,
        payment_count: 0
      }
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить сводку по типам оплат',
      life: 4000
    })
  } finally {
    loading.value = false
  }
}

function resetFilters() {
  dateFrom.value = null
  dateTo.value = null
  loadSummary()
}

watch([dateFrom, dateTo], () => {
  loadSummary()
})

onMounted(loadSummary)
</script>

<template>
  <div class="dashboard-tab-content">
  <section class="summary-filters-card">
    <div class="filter-field">
      <label>Дата с</label>
      <DatePicker
        v-model="dateFrom"
        dateFormat="dd.mm.yy"
        showIcon
        fluid
        appendTo="self"
        placeholder="Начало периода"
      />
    </div>

    <div class="filter-field">
      <label>Дата по</label>
      <DatePicker
        v-model="dateTo"
        dateFormat="dd.mm.yy"
        showIcon
        fluid
        appendTo="self"
        placeholder="Конец периода"
      />
    </div>

    <div class="filter-actions">
      <ButtonVue type="button" label="Сброс" severity="secondary" text @click="resetFilters" />
    </div>
  </section>

  <section class="table-card table-card--scroll">
    <DataTable
      :value="rows"
      :loading="loading"
      dataKey="payment_type"
      class="summary-table"
      scrollable
      scrollHeight="flex"
    >
      <ColumnTable field="payment_type" header="Тип оплаты" :style="{ minWidth: '220px' }">
        <template #body="{ data }">
          <span :class="{ 'summary-total': data.is_total }">{{ data.payment_type }}</span>
        </template>
      </ColumnTable>

      <ColumnTable field="sum_amount" header="SUM amount" :style="{ minWidth: '140px' }">
        <template #body="{ data }">
          <span :class="{ 'summary-total': data.is_total }">{{ formatMoney(data.sum_amount) }}</span>
        </template>
      </ColumnTable>

      <ColumnTable field="sum_commissions" header="SUM комиссий" :style="{ minWidth: '150px' }">
        <template #body="{ data }">
          <span :class="{ 'summary-total': data.is_total }">{{ formatMoney(data.sum_commissions) }}</span>
        </template>
      </ColumnTable>

      <ColumnTable field="commission_percent" header="%" :style="{ minWidth: '90px' }">
        <template #body="{ data }">
          <span :class="{ 'summary-total': data.is_total }">{{ `${formatPercent(data.commission_percent)}%` }}</span>
        </template>
      </ColumnTable>

      <template #empty>
        <div class="empty-hint">Нет данных за выбранный период.</div>
      </template>
    </DataTable>
  </section>

  <section class="table-card table-card--scroll">
    <DataTable
      :value="statusRows"
      :loading="loading"
      dataKey="label"
      class="summary-table"
      scrollable
      scrollHeight="flex"
    >
      <ColumnTable field="label" header="Показатель" :style="{ minWidth: '180px' }">
        <template #body="{ data }">{{ data.label }}</template>
      </ColumnTable>

      <ColumnTable field="sum_amount" header="SUM amount" :style="{ minWidth: '140px' }">
        <template #body="{ data }">{{ formatMoney(data.sum_amount) }}</template>
      </ColumnTable>

      <template #empty>
        <div class="empty-hint">Нет данных за выбранный период.</div>
      </template>
    </DataTable>
  </section>
  </div>
</template>

<style scoped>
.dashboard-tab-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-filters-card {
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  padding: 0.85rem;
  background: #ffffff;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: span 3;
}

.filter-field label {
  font-size: 0.8rem;
  color: #64748b;
}

.filter-actions {
  grid-column: span 3;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.table-card {
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  padding: 0.25rem;
  background: #ffffff;
}

.table-card--scroll {
  overflow-x: auto;
}

.summary-total {
  font-weight: 700;
}

.empty-hint {
  padding: 1.25rem 1rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}

@media (max-width: 1200px) {
  .summary-filters-card {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .filter-field,
  .filter-actions {
    grid-column: span 3;
  }
}
</style>
